import * as THREE from "three";
import {GLTFExporter} from 'three/examples/jsm/exporters/GLTFExporter';
import {GetHighestRidges, ShouldAddSetbackToRoof, checkSameLine} from "./PathwayControl";
import {testMesh} from "./Loader";
import {CheckInsidePos, RefreshShade} from "./RefreshShade";
import {inch2m} from "../../../../../Constants";
import JSZip from "jszip";
import {GetLatLon, SetMapMesh} from "./MapControl";
import {SetTerrainPosition} from "./TerrainControl";
// import { withLoadingMessage } from "../../../store/loadingMessageStore";
// import { ACCESS_MULTIPLIERS, SHADE_CALCULATIONS, siteCalculationState } from "../../../services/siteCalculationState";
// import { iterateCircular } from "../../../helpers/arrayIterate";
// import { logger } from "../../../services/loggingService";
import {useViewerStore} from "../../../../../store/store";
import {withLoadingMessage} from "../../../../../store/loadingMessageStore";
import {
    ACCESS_MULTIPLIERS,
    SHADE_CALCULATIONS,
    siteCalculationState
} from "../../../../../services/siteCalculationState";
import {iterateCircular} from "../../../../../helpers/arrayIterate";
import {logger} from "../../../../../services/loggingService";
import { SetDisplayShadePoints } from "./RefreshShade";
import { BuildingWise33PercentRuleInfo, FaceItem, LayerOption, ModuleData, ModuleMesh, RoofMesh, TreeGroup, XYZCoordinates } from "../../../../../@types/types";
import { Viewer } from "../Viewer-Factory/Viewer";

let mouse = new THREE.Vector2();
let raycaster = new THREE.Raycaster();

export function isMobile(): boolean {
    let check = false;
    const agent = navigator.userAgent;

    // Common mobile device checks
    if (/android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(agent)) {
        return true;
    }

    // Touch support as a fallback (for iPadOS 13+ and some tablets)
    if (('ontouchstart' in window || navigator.maxTouchPoints > 0) && /Macintosh/i.test(agent)) {
        check = true;
    }

    return check;
}

export const isIpadDevice = () =>  window.innerWidth <= 1366;

export function GetDis(pos0, pos1, dis3D = false) {
    if(!pos0 || !pos1) return 0;
    if (pos0.X && !pos0.x) {pos0.x = pos0.X; pos0.y = pos0.Y; pos0.z = pos0.Z;}
    if (pos1.X && !pos1.x) {pos1.x = pos1.X; pos1.y = pos1.Y; pos1.z = pos1.Z;}
    const disX = pos0.x - pos1.x, disY = pos0.y - pos1.y, disZ = pos0.z - pos1.z;
    if (dis3D) return Math.sqrt(Math.pow(disX, 2) + Math.pow(disY, 2) + Math.pow(disZ, 2));
    else return Math.sqrt(Math.pow(disX, 2) + Math.pow(disZ, 2));
}

export function GetClickObj(e: MouseEvent | TouchEvent, arr: THREE.Object3D[], camera: THREE.Camera, cSize: { w: number, h: number }) {
    const { posX, posY } = GetMousePos(e);
    mouse.x = (posX / cSize.w) * 2 - 1;
    mouse.y = -(posY / cSize.h) * 2 + 1;

    raycaster.setFromCamera(mouse, camera);
    const interInfo = raycaster.intersectObjects(arr, false)[0];
    return interInfo;
}

export function EmptyGroup(group: THREE.Group) {
    for (let i = group.children.length - 1; i >= 0; i--) {
        group.remove(group.children[i]);
    }
}

export function getCenterPoint(mesh: THREE.Mesh) {
    var geometry = mesh.geometry;
    geometry.computeBoundingBox();
    var center = new THREE.Vector3();
    geometry.boundingBox?.getCenter(center);
    mesh.localToWorld(center);
    return center;
}

export function SetVisibleGroup(self: Viewer, type: LayerOption, value: boolean) {
    const { roofMeshArr, obstGroup, treeGroup, roofGroup, mapCustom, treeLabelGroup, treeTagGroup, moduleMeshArr, roofTagGroup } = self;
    let meshArrayToHide: any[] = [];

    if (type === "roof" || type === "modules") {
        const isModuleMultiplierLabelVisible = useViewerStore.getState().isModuleMultiplierLabelVisible;
        if (isModuleMultiplierLabelVisible) {
            for (const module of moduleMeshArr) {
                if (type === "roof" && !module.visible) continue;
                if (type === "modules" && !roofMeshArr[0].visible) continue;
                for (const child of module.children) {
                    if (child.meshType === 'moduleLabel') {
                        child.visible = value;
                    }
                }
            }
        }
    }
    switch (type) {
        case 'roof':
            const floorArr = roofGroup.children[0]?.children.filter((child: any) => child.meshType === 'floor') || [];
            meshArrayToHide = [...roofMeshArr, ...floorArr];
            roofGroup.traverse((child: any) => {
                if (child.module) meshArrayToHide.push(child);
            });
            const isRoofTagVisible = useViewerStore.getState().isRoofTagVisible;
            if (isRoofTagVisible) {
                roofTagGroup.children.forEach(labelGroup => {
                    labelGroup.visible = value;
                })
            }
            break
        case 'pathway':
        case 'setback':
            roofGroup.children[0]?.traverse((child: any) => {
                if (child.meshType === type) meshArrayToHide.push(child);
            });
            break
        case 'modules':
            roofGroup.traverse((child: any) => {
                if (child.module) meshArrayToHide.push(child);
            });
            break
        case 'obstruction':
            meshArrayToHide = obstGroup.children;
            break
        case 'tree':
            meshArrayToHide = treeGroup.children;
            const isTreeLabelVisible = useViewerStore.getState().isTreeLabelVisible;
            const isTreeMultiplierLabelVisible = useViewerStore.getState().isTreeMultiplierLabelVisible;
            if (isTreeMultiplierLabelVisible) {
                treeLabelGroup.children.forEach(labelGroup => {
                    labelGroup.visible = value;
                })
            }
            if (isTreeLabelVisible) {
                treeTagGroup.children.forEach(tagGroup => {
                    tagGroup.visible = value;
                })
            }
            break;
        case 'irradiance':
            useViewerStore.setState({ moduleSolarAccess: value });
            SetDisplayShadePoints(self, null);
            break;
        case 'maps':
            self.mapView = value;
            SetMapMesh(self)
            break
        case 'terrain':
            self.terrainView = value;
            SetTerrainPosition(self);
            break
        default:
            logger.warn(`Unknown type: ${type}`);
            break;
    }
    meshArrayToHide.forEach(mesh => mesh.visible = value);
}

export function GetRadVal(deg: number) {
    return Math.PI / 180 * deg;
}

export function GetDegVal(rad: number, float = false) {
    if (float) return 180 / Math.PI * rad;
    else return Math.round(180 / Math.PI * rad);
}

export function GetRoundNum(val: number, num?: number) {
    if (!num) num = 0;
    return Math.round(val * Math.pow(10, num)) / Math.pow(10, num);
}

export function GetDateVal(date: Date) {
    const day = date.getDate(), month = date.getMonth() + 1, year = date.getFullYear();
    return new Date(`${year}/${month}/${day}`);
}

export function GetParentTool(e: Event) {
    const { target } = e as Event;
    const parentTool = (target as HTMLElement).closest('.canvas__toolbar');
    const controlTool = (target as HTMLElement).closest('.canvas-control-wrapper');
    return (parentTool || controlTool) ? true : false;
}

export function sortFacePosArr(posArr: XYZCoordinates[], azimuth: number) {
    var minY = Infinity;
    const lowPosArr: XYZCoordinates[] = [];
    posArr.forEach(pos => {
        if (pos.y < minY) minY = pos.y;
    });
    posArr.forEach(pos => {
        if (pos.y < minY + 0.2) lowPosArr.push(pos);
    });
    var lastPos: XYZCoordinates, minVal = Infinity, maxVal = -Infinity;
    lowPosArr.forEach(lowPos => {
        if (azimuth > 45 && azimuth <= 135 && lowPos.z > maxVal) {
            maxVal = lowPos.z;
            lastPos = lowPos;
        } else if (azimuth > 135 && azimuth <= 225 && lowPos.x < minVal) {
            minVal = lowPos.x;
            lastPos = lowPos;
        } else if (azimuth > 225 && azimuth <= 275 && lowPos.z < minVal) {
            minVal = lowPos.z;
            lastPos = lowPos;
        } else if ((azimuth > 275 || azimuth <= 45) && lowPos.x > maxVal) {
            maxVal = lowPos.x;
            lastPos = lowPos;
        }
    });
    const lastIdx = posArr.findIndex(pos => {
        return pos.x === lastPos.x && pos.y === lastPos.y && pos.z === lastPos.z
    });
    const newPosArr = [];
    for (let i = lastIdx + 1; i <= lastIdx + posArr.length; i++) {
        newPosArr.push(posArr[i % posArr.length]);
    }
    return newPosArr;
}


export function Get2DPos(obj: THREE.Object3D, cWidth: number, cHeight: number, camera: THREE.Camera) {
    var vector = new THREE.Vector3();
    var widthHalf = 0.5 * cWidth;
    var heightHalf = 0.5 * cHeight;
    obj.updateMatrixWorld();
    vector.setFromMatrixPosition(obj.matrixWorld);
    vector.project(camera);
    vector.x = (vector.x * widthHalf) + widthHalf;
    vector.y = -(vector.y * heightHalf) + heightHalf;
    return { x: vector.x, y: vector.y };
}



export async function DownloadGLTF(self: Viewer) {
    await siteCalculationState.useCalculations(SHADE_CALCULATIONS, ACCESS_MULTIPLIERS);

    const { scene } = self, exporter = new GLTFExporter();
    const mapJson = GetMapJson(self); // return;

    scene.traverse(child => {
        if (child.name === 'plane') child.visible = false;
    })
    scene.userData = { ...mapJson };

    const options = {
        onlyVisible: true,
        truncateDrawRange: true,
        binary: false,
        maxTextureSize: 4096
    };

    exporter.parse(scene, (gltf) => {
        if (gltf instanceof ArrayBuffer) {
            saveArrayBuffer(gltf, 'scene.glb');
        } else {
            withLoadingMessage(async () => {
                const salesforceId = sessionStorage.getItem("SalesForceId")

                const output = JSON.stringify(gltf), gltfName = `${salesforceId}.gltf`, zipName = `${salesforceId}.zip`;
                const zip = new JSZip();
                zip.file(gltfName, output);
                const content = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 9 } });
                save(content, zipName);
            }, { title: 'Exporting...' });
        }
        scene.traverse((child) => {
            if (child.name === 'plane') child.visible = false;
        });
    }, error => {
        logger.error(error);
    }, options);
}


export async function ExportGen(self: Viewer, getJson?: boolean) {
    await siteCalculationState.useCalculations(SHADE_CALCULATIONS, ACCESS_MULTIPLIERS);
    const genJson = GetGenJson(self);
    if (!genJson) return;
    if (getJson) {
        return genJson;
    }
    const jsonStr = EncryptJsonStr(JSON.stringify(genJson));
    return new Blob([jsonStr], { type: 'text/plain;charset=utf-8' });
}

function save(blob: Blob, filename: string) {
    const link = document.createElement('a');
    link.style.display = 'none';
    document.body.appendChild(link);
    setTimeout(() => {
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
    }, 100);
}

function saveArrayBuffer(buffer: ArrayBuffer, filename: string) {
    save(new Blob([buffer], { type: 'application/octet-stream' }), filename);
}

// export function CheckContact(obj0: THREE.Object3D, box1: THREE.Box3) {
//     const box0 = new THREE.Box3().setFromObject(obj0);
//     return box0.intersectsBox(box1);
// }

// export function CheckContactArr(objArr: THREE.Object3D[], obj: any, key: string) {
//     var contactObj: any;
//     const boxObj = new THREE.Box3().setFromObject(obj);
//     objArr.forEach((item: any) => {
//         if (item.visible === false || contactObj) return;
//         if (key && obj[key] && item[key] === obj[key]) return;
//         if (CheckContact(item, boxObj)) contactObj = item;
//     });
//     return contactObj;
// }

// Check for obstruction overlapping with other obstructions or modules on the roof
export function CheckOverObstArr(obstGroup: THREE.Group, moduleMeshArr: THREE.Mesh[], roofId: string, verInfo: any[], obstId: string) {
    const roofItemArr: any[] = [];
    // Gather corner vertexes of modules and obstructions on the same roof
    moduleMeshArr.forEach((item: any) => {
        if (item.roofId !== roofId) return;
        const { wPosArr } = item;
        roofItemArr.push([wPosArr[0], wPosArr[1], wPosArr[3], wPosArr[2]]);
    });
    obstGroup.children.forEach((item: any) => {
        if (item.children[0].children[0].children[0].roofId !== roofId) return;
        if (obstId && item.obstId === obstId) return;
        roofItemArr.push([...item.children[0].children[0].children[0].verPosArr]);
    });
    // Check if any vertex of the new obstruction is inside existing items or vice versa
    var flag = false;
    roofItemArr.forEach(item => {
        if (flag) return;
        item.forEach((pos: any) => {
            if (CheckInsidePos(verInfo, pos)) flag = true;
        });
    });
    if (flag) return true;
    verInfo.forEach(ver => {
        if (flag) return;
        roofItemArr.forEach(item => {
            if (CheckInsidePos(item, ver)) flag = true;
        });
    });
    return flag;
}

export function GetVerPosArr(obst: any) {
    const obsMesh = obst.children[0].children[0].children[0], verPosArr: any[] = [];
    [{ x: -1, z: -1 }, { x: -1, z: 1 }, { x: 1, z: 1 }, { x: 1, z: -1 }].forEach(dir => {
        const cloneTest = testMesh.clone();
        cloneTest.position.set(dir.x / 2, 0, dir.z / 2);
        obsMesh.add(cloneTest);
        const wPos = cloneTest.getWorldPosition(new THREE.Vector3());
        verPosArr.push(wPos);
        obsMesh.remove(cloneTest);
    });
    return verPosArr;
}

export function GetRangeKey(rangeKey: string) {
    var realKey = ''
    if (rangeKey.includes('customImage')) {
        const subKey = rangeKey.substring(11);
        const firstStr = subKey.substring(0, 1), otherStr = subKey.substring(1);
        realKey = firstStr.toLowerCase() + otherStr;
    } else if (rangeKey.includes('Obst')) {
        realKey = rangeKey.substring(4).toLowerCase();
    } else if (rangeKey.includes('Tree')) {
        if (rangeKey === 'TreeTotalHeight') realKey = 'height';
        else if (rangeKey === 'TreeCrownDiameter') realKey = 'crown_radius';
        else if (rangeKey === 'TreeCrownHeight') realKey = 'crown_height';
        else if (rangeKey === 'TreeTrunkRadius') realKey = 'trunk_radius';
    }
    return realKey
}


export function SortArray<T>(arr: T[], key: keyof T): T[] {
    return arr.slice().sort((a, b) => {
        if (a[key] == null && b[key] == null) return 0;
        if (a[key] == null) return 1;
        if (b[key] == null) return -1;
        if (a[key] < b[key]) return -1;
        if (a[key] > b[key]) return 1;
        return 0;
    });
}

function GetNodeArr(posArr: any[], nodes: any[]) {
    return posArr.map(pos =>
        nodes.findIndex(node =>
            node.X === pos.x && node.Y === -pos.z && node.Z === pos.y
        )
    );
}

export function getRoofModuleMeshes(roof: RoofMesh): ModuleMesh[] {
    return roof.children.filter(isModuleMesh) as ModuleMesh[];
}

export function getModuleMeshInfo(module: ModuleMesh): ModuleData {
    const { isBallastedRackType, ballastRackDirection, isBallastFlipped } = module.userData;
    return {
        dir: module.dir,
        moduleId: module.moduleId,
        overPathIds: module.overPaths,
        selSize: module.selSize,
        x: module.position.x,
        z: module.position.y,
        isBallastedRackType: isBallastedRackType || false,
        ballastRackDirection: ballastRackDirection || 0,
        isBallastFlipped: isBallastFlipped || false,
    };
}

export function getRoofModuleCount(roof: RoofMesh) {
    return roof.children.reduce((p: any, c: any) => p + isModuleMesh(c), 0);
}

export function isModuleMesh(mesh: any): boolean {
    return !!mesh.module;
}

export function GetMapJson(self: Viewer) {
    const { roofs, wallHEdit, treeGroup, roofMeshArr, obstGroup, mapCoordinates, sunInfo, irrCSVMainData } = self;
    const isRoofSquareDataUnavailable = useViewerStore.getState().isRoofSquareDataUnavailable;

    if (!roofs || !roofs[0]) return;
    const irradianceMonth = self.irradianceMonth;// this is drawn from store. Earlier it was read from face;
    const { nodes, edges, faces } = roofs[0];
    const nodeArr: any[] = [], edgeArr: any[] = [], faceArr: any[] = [], obstArr: any[] = [], treeArr: any[] = [];
    const buildings: any = {};
    nodes.forEach((node: any) => {
        const { X, Y, Z } = node;
        nodeArr.push({ X, Y: -Y, Z: Z });
    });
    edges.forEach((edge: any) => {
        const { posArr, type } = edge;
        edgeArr.push({ nodes: GetNodeArr(posArr, nodeArr), type });
    });

    faces.forEach((face: any, idx: number) => {
        const { posArr, oriAng, size, holes, lines, ptHashArr, ptHashAverage, ptHashMonth, roofFaceId, posFlatArr } = face, { azimuth, tilt } = oriAng;
        const selRoof = roofMeshArr[idx], { userData } = selRoof, moduleCount = userData.moduleCount || { land: 0, port: 0, both: 0 };
        const shadeInfo: any[] = [];

        selRoof.userData.shadeArrForRoof.forEach((child: any) => {
            const { onModule, monthArr, ptHashVal } = child;
            shadeInfo.push({ onModule, monthArr, ptHashVal });
        })

        const modules = getRoofModuleMeshes(selRoof);

        buildings[selRoof.userData.buildingName] = {
            buildingType: selRoof.flatRoof ? "GroundMount" : "Common",
            houseId: selRoof.houseId,
            name: selRoof.userData.buildingName,
            wallHeight: wallHEdit,
            isFlatRoofBuilding: selRoof.flatRoof === true,
            isDetachedStructure: useViewerStore.getState().detachedStructureBuildingIds.includes(Number(selRoof.userData.buildingName)),
        };

        const faceItem: FaceItem = {
            shell: GetNodeArr(posArr, nodeArr),
            azimuth,
            pitch: tilt,
            flatRoof: selRoof.flatRoof === true,
            sq_footage: size,
            holes: [],
            moduleArr: modules.map((m: any) => getModuleMeshInfo(m)),
            moduleCount,
            monthlyWeightArr: face.monthlyWeightArr ?? [],
            lines,
            ptHashArr,
            ptHashAverage,
            ptHashMonth,
            shadeInfo,
            roofFaceId,
            roof_tag_id: userData.roof_tag_id,
            houseId: selRoof.houseId,
            buildingName: selRoof.userData.buildingName,
            textureId: userData.textureId,
            color: userData.color,
            questionableRoof: userData.questionableRoof,
            questionableRoofId: userData.questionableRoofId || 0,
            questionableRoofNotes: userData.questionableRoofNotes || "",
            height: userData.initialHeight,
            normalSquare: isRoofSquareDataUnavailable ? null : userData?.roofSquare?.normalSquare ?? null,
            lowSquare: isRoofSquareDataUnavailable ? null : userData?.roofSquare?.lowSquare ?? null,
            isRoofSquareActive: isRoofSquareDataUnavailable ? true : userData?.roofSquare?.isRoofSquareActive ?? true,
            pitchOverride: userData.pitchOverride,
            posFlatArr: posArr,
            roofSlope: selRoof.oriAng.tilt,
            roofArea: selRoof.size,
        };

        if (selRoof.flatRoof) {
            faceItem.carport = face.carport;
            faceItem.surfaceMount = face.surfaceMount;
            faceItem.scl = face.scl;
            faceItem.ppInfo = face.ppInfo;
            faceItem.irrDir = face.irrDir;
            faceItem.pos = face.pos;
            faceItem.buildingName = face.buildingName;
        }


        if (holes && holes.length) {
            faceItem.holes = [];
            holes.forEach((hole: any) => {
                faceItem.holes.push(GetNodeArr(hole.posArr, nodeArr));
            });
        }
        faceArr.push(faceItem);
    });
    obstGroup.children.forEach((obst: any) => {
        const { position, children, roofId } = obst;
        const { shapeNum, radius, verFloor, height, width, length } = children[0].children[0].children[0];
        const normal_top = verFloor ? { X: 0, Y: 0, Z: 1 } : { X: 0.5, Y: 0.5, Z: 0.5 };
        let type;
        const pos = { x: position.x, y: position.y + height, z: position.z };
        var nodes = children[0].children[0].children[0].userData.nodes ?? [];

        // Map shapeNum to type using an array for efficiency and clarity
        const shapeTypes = ['polygon', 'circle', 'cylinder', 'default-polygon'];
        type = shapeTypes[shapeNum - 1] || 'default-polygon';

        const roof = roofMeshArr.find(roof => roof.roofFaceId === obst.roofId) || roofMeshArr[0];
        const roofGroup = roof.parent.parent.parent;
        const diff = new THREE.Vector3(
            roofGroup.position.x - roofGroup.userData.originalPosition.x,
            roofGroup.position.y - roofGroup.userData.originalPosition.y,
            roofGroup.position.z - roofGroup.userData.originalPosition.z
        );

        obstArr.push({
            radius: radius / 2,
            type,
            isCustom: !obst.isDefaultObstruction,
            userUpdatedDefaultDimensions: obst.userUpdatedDefaultDimensions,
            x: pos.x - diff.x,
            y: -pos.z - diff.z,
            z: pos.y - diff.y,
            height,
            width,
            length,
            normal_top,
            nodes,
            roofId,
            delStatus: obst.children[0].children[0].children[0].delStatus,
        });
    });
    (treeGroup.children as TreeGroup[]).forEach((tree: TreeGroup) => {
        const { position, children, userData } = tree, crownMesh = children[0], trunkMesh = children[1], { incrossArr } = children[2] as any;
        // const {crownScl} = crownMesh;
        const crown_radius = userData.crown_radius / 2; // crownMesh.scale.x / crownScl.x / 2;
        const crown_height = userData.crown_height; // crownMesh.scale.y / crownScl.y;
        const trunk_radius = userData.trunk_radius; // trunkMesh.scale.x
        const height = tree.totalH;
        const { latitude, longitude } = GetLatLon(self.mapCoordinates, {x: position.x, z: position.z});
        treeArr.push({
            crown_radius: crown_radius,
            crown_height,
            trunk_radius,
            height,
            type: 'ellipsoid',
            x: position.x,
            y: -position.z,
            latitude: latitude,
            longitude: longitude,
            incrossArrLength: incrossArr?.length ?? 0,
            delStatus: tree.delStatus,
            crownKey: crownMesh.userData.crownKey,
        });
    });

    const localAngIdealArr = useViewerStore.getState().savedRoofWiseIdealMultiplier;
    const localFacePtHashArr = useViewerStore.getState().savedRoofWiseAccessMultiplierDataArray;
    const localAngMultiplierInfo = useViewerStore.getState().savedAccessMultipliersForSolarRoofs;

    const { imageryType, customImageScale, customImageRotation } = useViewerStore.getState();

    const mapJson = {
        location: null,
        mapPosition: self.mapCustom ? {
            x: self.mapCustom.position.x,
            z: self.mapCustom.position.z
        } : { x: 0, z: 0 },
        mapProperties: {
            imageryType,
            scale: customImageScale,
            rotation: customImageRotation,
        },
        obstructions: obstArr,
        origin: { ...mapCoordinates },
        buildings: Object.values(buildings),
        roofs: [{ nodes: nodeArr, edges: edgeArr, faces: faceArr }],
        trees: treeArr,
        sunInfo,
        weatherStation: self.weatherStationName,
        irrCSVMainData,
        irradianceMonth,
        localAngIdealArr,
        localFacePtHashArr,
        localAngMultiplierInfo,
        roofBaseHeight: useViewerStore.getState().wallEditedHeight,
        roofOriginalHeight: useViewerStore.getState().wallInitialHeight,
        isSetbackOverrideEnabled: useViewerStore.getState().isSetbackOverride,
        deactivatedRoofIdArr: useViewerStore.getState().deactivatedRoofIdArr,
    }
    return mapJson;
}

function GetNearPosIdx(ptArr: any[], pos: any) {
    var minDis = Infinity, selIdx = -1;
    ptArr.forEach((pt, ptIdx) => {
        const dis = GetDis(pt.pos, pos, true);
        if (dis < minDis) { minDis = dis; selIdx = ptIdx; }
    });
    return selIdx;
}

const verCountTree = 8, angUnitTree = Math.PI * 2 / verCountTree, verNorArr: Array<{ x: number, z: number }> = [];
for (let i = 0; i < verCountTree; i++) {
    const ang = angUnitTree * i;
    verNorArr.push({ x: Math.cos(ang), z: Math.sin(ang) });
}

export function GetSizePosArr(posArr: any[]) {
    var minX = Infinity, minY = Infinity, minZ = Infinity, maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
    posArr.forEach(pos => {
        const { x, y, z } = pos;
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
        if (z < minZ) minZ = z;
        if (z > maxZ) maxZ = z;
    });
    const size = { x: maxX - minX, y: maxY - minY, z: maxZ - minZ },
        cPos = { x: (maxX + minX) / 2, y: (maxY + minY) / 2, z: (maxZ + minZ) / 2 },
        norPos = new THREE.Vector3(cPos.x, cPos.y, cPos.z).normalize(),
        minMax = { minX, maxX, minY, maxY, minZ, maxZ };
    return { size, cPos, norPos, minMax };
}

export function doLinesIntersect(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, d: THREE.Vector3, segmentAB: boolean, segmentCD: boolean, maxDistance: number) {
    const epsilon = 0.01;

    const s = (c.y * b.x - c.y * a.x - a.y * b.x + a.y * a.x - c.x * b.y + c.x * a.y + a.x * b.y - a.x * a.y)
        / ((d.x - c.x) * (b.y - a.y) - (d.y - c.y) * (b.x - a.x));
    const t = b.y !== a.y
        ? (c.y + s * (d.y - c.y) - a.y) / (b.y - a.y)
        : (c.x + s * (d.x - c.x) - a.x) / (b.x - a.x);

    const rx = a.x + t * (b.x - a.x);
    const ry = a.y + t * (b.y - a.y);
    const rz = a.z + t * (b.z - a.z);

    const qx = c.x + s * (d.x - c.x);
    const qy = c.y + s * (d.y - c.y);
    const qz = c.z + s * (d.z - c.z);

    const dx = rx - qx;
    const dy = ry - qy;
    const dz = rz - qz;
    const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);

    if (dist < maxDistance) {
        if ((!segmentAB || t >= -epsilon && t <= 1 + epsilon) && (!segmentCD || s >= -epsilon && s <= 1 + epsilon)) {
            return new THREE.Vector3(qx, qy, qz);
        }
    }

    return null;
}


const EPSILON = 1e-4;

function near(a: number, b: number, epsilon = EPSILON) {
    return Math.abs(a - b) < epsilon;
}


export function parseExtrudedPolygon(geometry: any) {
    geometry.computeBoundingBox();
    geometry.computeVertexNormals();

    const { min, max } = geometry.boundingBox;
    const index = geometry.index;
    const posAttr = geometry.attributes.position;

    const topTris = [];
    const bottomTris = [];
    const sideTris = [];

    for (let i = 0; i < index.count; i += 3) {
        const iA = index.array[i];
        const iB = index.array[i + 1];
        const iC = index.array[i + 2];

        const va = new THREE.Vector3(
            posAttr.array[iA * 3], posAttr.array[iA * 3 + 1], posAttr.array[iA * 3 + 2]
        );
        const vb = new THREE.Vector3(
            posAttr.array[iB * 3], posAttr.array[iB * 3 + 1], posAttr.array[iB * 3 + 2]
        );
        const vc = new THREE.Vector3(
            posAttr.array[iC * 3], posAttr.array[iC * 3 + 1], posAttr.array[iC * 3 + 2]
        );

        const allMin = near(va.y, min.y) && near(vb.y, min.y) && near(vc.y, min.y);
        const allMax = near(va.y, max.y) && near(vb.y, max.y) && near(vc.y, max.y);

        if (allMin) {
            bottomTris.push(va, vb, vc);
        } else if (allMax) {
            topTris.push(va, vb, vc);
        } else {
            sideTris.push(va, vb, vc);
        }
    }

    return { topTris, bottomTris, sideTris };
}



function addTriangleAsFace(va: THREE.Vector3, vb: THREE.Vector3, vc: THREE.Vector3, newLineArr: any[], newFaceArr: any[], newPtArr: any[], lineIndex: number, ptIndex: number) {
    const ptKeyA = 'P' + ptIndex;
    const ptKeyB = 'P' + (ptIndex + 1);
    const ptKeyC = 'P' + (ptIndex + 2);

    newPtArr.push({ ptKey: ptKeyA, pos: { x: va.x, y: va.y, z: va.z } });
    newPtArr.push({ ptKey: ptKeyB, pos: { x: vb.x, y: vb.y, z: vb.z } });
    newPtArr.push({ ptKey: ptKeyC, pos: { x: vc.x, y: vc.y, z: vc.z } });

    const lineKeyAB = 'L' + lineIndex;
    const lineKeyBC = 'L' + (lineIndex + 1);
    const lineKeyCA = 'L' + (lineIndex + 2);

    newLineArr.push({
        lineKey: lineKeyAB,
        posArr: [{ x: va.x, y: va.y, z: va.z }, { x: vb.x, y: vb.y, z: vb.z }],
        ptKeyArr: [ptKeyA, ptKeyB],
        STREET_SIDE: false,
        ENFORCED_PATHWAY: false,
        ENFORCED_ROOFS: null,
        TYPE: null
    });
    newLineArr.push({
        lineKey: lineKeyBC,
        posArr: [{ x: vb.x, y: vb.y, z: vb.z }, { x: vc.x, y: vc.y, z: vc.z }],
        ptKeyArr: [ptKeyB, ptKeyC],
        STREET_SIDE: false,
        ENFORCED_PATHWAY: false,
        ENFORCED_ROOFS: null,
        TYPE: null
    });
    newLineArr.push({
        lineKey: lineKeyCA,
        posArr: [{ x: vc.x, y: vc.y, z: vc.z }, { x: va.x, y: va.y, z: va.z }],
        ptKeyArr: [ptKeyC, ptKeyA],
        STREET_SIDE: false,
        ENFORCED_PATHWAY: false,
        ENFORCED_ROOFS: null,
        TYPE: null
    });

    const faceLineArr = [lineKeyAB, lineKeyBC, lineKeyCA];
    const facePosArr = [{ x: va.x, y: va.y, z: va.z }, { x: vb.x, y: vb.y, z: vb.z }, { x: vc.x, y: vc.y, z: vc.z }];
    const facePtArr = [ptKeyA, ptKeyB, ptKeyC];

    newFaceArr.push({
        faceLineArr,
        posArr: facePosArr,
        size: null,
        facePtArr
    });

    return { usedLines: 3, usedPoints: 3 };
}


const getBallastDir = (moduleDir: string, isFlipped: boolean) => {
    return moduleDir === 'land' ? (isFlipped ? 'S' : 'N') : (isFlipped ? 'E' : 'W');
};



function GetGenJson(self: Viewer) {
    const {
        roofs,
        roofGroup,
        treeGroup,
        obstGroup,
        mapCoordinates,
        buildingAlt,
        addressInfo,
        roofMeshArr,
        moduleMeshArr,
        csvFileName,
        sunInfo,
        sunVecArr,
        map64Binary,
        irradianceMonth
    } = self; //for removing redundancy in irradianceMonth
    const { latitude, longitude } = mapCoordinates, sunAngles: any = {};
    const { state, city, detail, postalCode } = addressInfo;
    const angMultiplierArr = useViewerStore.getState().savedAccessMultipliersForSolarRoofs;
    const idealProdMulti = useViewerStore.getState().savedAnnualIdealMultiplier;


    //get points of setback mesh
    const setbackPts: any[] = [];

    const { edges, faces } = roofs[0];
    const edgeObj: any = { EAVE: 0, HIP: 0, RAKE: 0, RIDGE: 0, STEPFLASH: 0, VALLEY: 0 }, moduleCountObj: any = {};
    const moduleList = useViewerStore.getState().moduleList;
    const selModuleInfo = useViewerStore.getState().activeModule || {};
    const selModuleIdx = moduleList.findIndex(m => m.text === selModuleInfo.text);
    Object.keys(edgeObj).forEach(key => {
        edgeObj[key] = edges.filter((edge: any) => edge.type === key.toLocaleLowerCase()).length;
    });
    const {EAVE, HIP, RAKE, RIDGE, STEPFLASH, VALLEY} = edgeObj;
    moduleMeshArr.forEach(module => {
        const moduleIdx = module.selSize.key;
        if (!moduleCountObj[moduleIdx]) moduleCountObj[moduleIdx] = 0;
        moduleCountObj[moduleIdx]++;
    });

    sunVecArr.forEach((month: any, mIdx: number) => {
        sunAngles[(mIdx + 1).toString()] = {};
        month.forEach((day: any, dIdx: number) => {
            sunAngles[(mIdx + 1).toString()][(dIdx + 1).toString()] = {};
            day.forEach((hour: any, hIdx: number) => {
                const norPos = new THREE.Vector3(hour.x, hour.y, hour.z).normalize();
                sunAngles[(mIdx + 1).toString()][(dIdx + 1).toString()][hIdx.toString()] = [norPos.x, -norPos.z, norPos.y];
            });
        });
    });

    roofMeshArr.forEach((roofMesh: any, rIdx: number) => {
        const { posArr } = roofMesh;
        const testPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
        testPlane.setFromCoplanarPoints(
            new THREE.Vector3(posArr[0].x, posArr[0].y, posArr[0].z),
            new THREE.Vector3(posArr[1].x, posArr[1].y, posArr[1].z),
            new THREE.Vector3(posArr[2].x, posArr[2].y, posArr[2].z),
        );
        roofMesh.planeNor = testPlane.normal;
        var minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity, maxY = -Infinity;
        posArr.forEach((pos: any) => {
            if (pos.x < minX) minX = pos.x; if (pos.x > maxX) maxX = pos.x;
            if (pos.z < minZ) minZ = pos.z; if (pos.z > maxZ) maxZ = pos.z;
            if (pos.y > maxY) maxY = pos.y;
        });
        const centerPos = { x: (minX + maxX) / 2, y: maxY, z: (minZ + maxZ) / 2 };
        roofMesh.roofTagPt = { x: centerPos.x / inch2m, y: -centerPos.z / inch2m, z: centerPos.y / inch2m };
    });

    roofMeshArr.forEach((roofMesh: any) => {
        roofMesh.genPosArr = []; roofMesh.appPosArr = [];
        roofMesh.pos2DArr.forEach((pos2D: any) => {
            const cloneTest = testMesh.clone();
            cloneTest.position.set(pos2D.x, pos2D.z, pos2D.y);
            roofMesh.add(cloneTest);
            const wPos = cloneTest.getWorldPosition(new THREE.Vector3());
            const roundPos = { x: GetRoundNum(wPos.x, 4), y: GetRoundNum(wPos.y, 4), z: GetRoundNum(wPos.z, 4) }
            roofMesh.genPosArr.push({ ...roundPos });
            roofMesh.appPosArr.push({ ...roundPos });
            roofMesh.remove(cloneTest);
        });
    });

    roofMeshArr.forEach((roofMesh, rIdx) => {
        if (rIdx === roofMeshArr.length - 1) return;
        roofMesh.genPosArr.forEach((selPos: any, sIdx: number) => {
            if (selPos.close === true) return;
            const nearPosArr = [{ ...selPos, rIdx, pIdx: sIdx }];
            for (let i = rIdx + 1; i < roofMeshArr.length; i++) {
                roofMeshArr[i].genPosArr.forEach((otherPos: any, oIdx: number) => {
                    if (otherPos.close === true) return;
                    const dis = GetDis(selPos, otherPos, true);
                    if (dis < 0.05) {
                        otherPos.close = true; selPos.close = true;
                        nearPosArr.push({ ...otherPos, rIdx: i, pIdx: oIdx });
                    }
                });
            }
            if (selPos.close && nearPosArr.length > 1) {
                const nearCount = nearPosArr.length;
                type Axis = "x" | "y" | "z";
                const tPos: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 };
                nearPosArr.forEach(nearPos => {
                    (['x', 'y', 'z'] as Axis[]).forEach(axis => {
                        tPos[axis] += nearPos[axis];
                    });
                });
                const cPos = { x: tPos.x / nearCount, y: tPos.y / nearCount, z: tPos.z / nearCount };

                const cloneTest = testMesh.clone();
                cloneTest.position.set(cPos.x, cPos.y, cPos.z);
                roofGroup.add(cloneTest);
                nearPosArr.forEach(nearPos => {
                    const { rIdx, pIdx } = nearPos;
                    (['x', 'y', 'z'] as Axis[]).forEach(axis => {
                        roofMeshArr[rIdx].genPosArr[pIdx][axis] = cPos[axis];
                    });
                });
                roofGroup.remove(cloneTest);
            }
        });
    });

    const ROOFS: { [key: string]: any } = {}, FACES: { [key: string]: any } = {}, LINES: { [key: string]: any } = {}, PTS: any = {}, TREES = {}, OBSTRUCTIONS: any = {}, MODULES: any = {},
        facesArr: any[] = [], lineArr: any[] = [], ptArr: any[] = [];
    type LayoutEntry = [[number, number, number], string];
    faces.forEach((face: any, faceIdx: number) => {
        const {
            oriAng,
            pos2DArr,
            roofFaceId,
            shadePosArr,
            ptHashArr,
            ptHashAverage,
            lines,
            size, azimuth, pitch
        } = face, selRoofMesh = roofMeshArr[faceIdx], { userData, roofTagPt, genPosArr } = selRoofMesh, MODULES: any[] = [];

        const LAYOUTS: {
            base_pts: LayoutEntry[];
            portrait: LayoutEntry[];
            landscape: LayoutEntry[];
        } = { base_pts: [], portrait: [], landscape: [] };
        const SHADE_PTS: Record<string, number> = {};
        const PRODUCTION_MULTIPLIER: Record<string, number> = {};
        const SHADE_WEIGHTS: Record<string, Record<string, any>> = {},
            faceIdxStr = faceIdx.toString(), FACE = 'F' + faceIdxStr;

        getRoofModuleMeshes(selRoofMesh).forEach((module) => {
            const moduleIdx = moduleMeshArr.findIndex(item => module.moduleId === item.moduleId);
            const worldPos = module.getWorldPosition(new THREE.Vector3());
            const dirKey = module.dir === 'port' ? 'portrait' : 'landscape';
            const dirKeyShort = module.dir === 'port' ? 'port' : 'land';
            MODULES.push('M' + moduleIdx);
            LAYOUTS[dirKey].push([[worldPos.x / inch2m, -worldPos.z / inch2m, worldPos.y / inch2m], dirKeyShort]);
        });

        const pos2DSize = GetSizePosArr(pos2DArr).size, WIDTH = pos2DSize.x / inch2m, HEIGHT = pos2DSize.z / inch2m;
        shadePosArr.forEach((shadePos: { x: number; y: number; z: number }, shadeIdx: number) => {
            const { x, y, z } = shadePos, inchX = x / inch2m, inchY = y / inch2m, inchZ = z / inch2m,
                shadeKey = JSON.stringify([inchX, inchY, inchZ]);
            SHADE_PTS[shadeKey] = ptHashArr[shadeIdx];
        });
        const selMultipliers = angMultiplierArr.find(item => item.azimuth === oriAng.azimuth && item.tilt === oriAng.tilt);
        if (selMultipliers && selMultipliers.multipliers) {
            selMultipliers.multipliers.forEach(item => {
                const { access, multiplier } = item;
                if (!access) return;
                PRODUCTION_MULTIPLIER[access.toString()] = multiplier;
            });
            const selMultiArr = selMultipliers.multipliers.find(item => item.access === Math.round(ptHashAverage));
            PRODUCTION_MULTIPLIER.default = selMultiArr ? selMultiArr.multiplier : 0;
        }
        irradianceMonth.forEach((month: any, monthIdx: number) => {
            SHADE_WEIGHTS[(monthIdx + 1).toString()] = {}
            month.forEach((hour: any, hourIdx: number) => {
                SHADE_WEIGHTS[(monthIdx + 1).toString()][(hourIdx + 1).toString()] = { ...hour };
            });
        });

        const roof_ID = userData.roof_tag_id;
        const rooftagPointAsArray = [roofTagPt.x, roofTagPt.y, roofTagPt.z];


        // ROOF
        ROOFS['R' + faceIdx] = {
            DESIGNATION: roof_ID,
            FACE,
            MODULES,
            LAYOUTS,
            WIDTH,
            HEIGHT,
            AZIMUTH: azimuth,
            TILT: pitch,
            SALESFORCE_ROOF_OBJECT_ID: roofFaceId,
            PRODUCTION_MULTIPLIER,
            SHADE_PTS,
            SHADE_WEIGHTS,
            TAG_PT: rooftagPointAsArray,
        }

        const faceLineArr: any[] = [], facePtArr: any[] = [];
        const uniquePts: Record<string, string> = {};
        genPosArr.forEach((pos: { x: number; y: number; z: number }) => {
            let ptKey = uniquePts[`${pos.x}${pos.y}${pos.z}`];
            if (!ptKey) {
                ptKey = `P${ptArr.length}`;
                ptArr.push({ ptKey: ptKey, pos: { x: pos.x, y: pos.y, z: pos.z } });

                uniquePts[`${pos.x}${pos.y}${pos.z}`] = ptKey;
            }

            facePtArr.push(ptKey);
        });

        lines.forEach((line: { points: any[]; realPoints: any[] }) => {
            line.realPoints = [];
            line.points.forEach(linePos => {
                const existPosIdx = GetNearPosIdx(ptArr, linePos);
                line.realPoints.push(ptArr[existPosIdx].pos);
            });
        });
        //ROOF LINES
        lines.forEach((line: { realPoints: any[]; streetSide: any; enforcedPathway: any; type: any; }) => {
            const { realPoints, streetSide, enforcedPathway } = line;
            var existLineKey: string | boolean = false;
            lineArr.forEach(line => {
                if (existLineKey) return;
                if (checkSameLine(realPoints, line.posArr)) existLineKey = line.lineKey;
            });
            if (!existLineKey) {
                const lineKey = 'L' + lineArr.length, ptKeyArr: string[] = [];
                realPoints.forEach(newLinePos => {
                    const existPosIdx = GetNearPosIdx(ptArr, newLinePos);
                    ptKeyArr.push(ptArr[existPosIdx].ptKey)
                });
                const newLineItem = { lineKey, posArr: realPoints, ptKeyArr, STREET_SIDE: streetSide, ENFORCED_PATHWAY: enforcedPathway, ENFORCED_ROOFS: null, TYPE: line.type.toUpperCase() }
                lineArr.push(newLineItem);
                existLineKey = lineKey;
            }
            faceLineArr.push(existLineKey);
        });
        //PATHWAY LINES
        selRoofMesh.children.filter((child: any) => child.pathActive === true).forEach((child: any) => {
            const wPosArr: THREE.Vector3[] = [];
            [0, 1].forEach(posIdx => {
                const pos = child.pathwayPosArr[posIdx];
                const cloneTest = testMesh.clone();
                cloneTest.position.set(pos.x, pos.z, pos.y); // Z/Y swap for world coords
                selRoofMesh.add(cloneTest);
                const wPos = cloneTest.getWorldPosition(new THREE.Vector3());
                wPosArr.push(wPos);
                selRoofMesh.remove(cloneTest);
            });

            // Convert to GENESIS-format point arrays
            const formattedLine = [
                [
                    GetRoundNum(wPosArr[0].x / inch2m, 4),
                    GetRoundNum(-wPosArr[0].z / inch2m, 4),
                    GetRoundNum(wPosArr[0].y / inch2m, 4)
                ],
                [
                    GetRoundNum(wPosArr[1].x / inch2m, 4),
                    GetRoundNum(-wPosArr[1].z / inch2m, 4),
                    GetRoundNum(wPosArr[1].y / inch2m, 4)
                ]
            ];

            setbackPts.push(formattedLine);
        });
        facesArr.push({ faceLineArr, posArr: genPosArr, size, facePtArr });
    });

    //TREE
    // treeGroup.children.forEach((tree, treeIdx) => {
    //     const treeFaceKeyArr = [];
    //     for (let i = 0; i < verCountTree + 2; i++) {
    //         treeFaceKeyArr.push('F' + (facesArr.length + i))
    //     }
    //     const {newFaceArr, newLineArr, newPtArr} = GetTreeInfo(tree, facesArr.length, lineArr.length, ptArr.length);
    //     facesArr.push(...newFaceArr);
    //     lineArr.push(...newLineArr);
    //     ptArr.push(...newPtArr);
    //     TREES['Tg' + treeIdx] = {FACES: treeFaceKeyArr}
    // });

    //OBSTRUCTION
    var obstNum = 0;
    obstGroup.children.forEach((obst: any) => {
        const isGhosted = obst.children[0].children[0].children[0].delStatus
        if (isGhosted) return;
        const { children, roofId } = obst, selRoof = roofMeshArr.find(roof => roof.roofFaceId === roofId);
        const { newFaceArr, newLineArr, newPtArr } = GetObstInfo(children[0].children[0].children[0], lineArr.length, ptArr.length, selRoof);
        newFaceArr.forEach((item) => {
            facesArr.push(item);
            // OBSTRUCTIONS['O' + obstNum] = { FACE: "F" + parseInt(facesArr.length - 1) };
            // OBSTRUCTIONS['O' + obstNum] = { FACE: "F" + (facesArr.length - 1) }; // parseInt not needed
            OBSTRUCTIONS['O' + obstNum] = { FACE: "F" + parseInt((facesArr.length - 1).toString()) };
            obstNum++;
        });
        lineArr.push(...newLineArr);
        ptArr.push(...newPtArr);
    });

    //MODULES
    moduleMeshArr.forEach((module, idx) => {
        const {wPosArr, irrPro, dir, multiplier, efficiency, roofId, position} = module, CORNER_PTS : number[][] = [];
        const parentRoof = roofMeshArr.find(roofMesh => roofMesh.roofFaceId===roofId) || {};
        const planeNor = parentRoof.planeNor || {};
        wPosArr.forEach((wPos: any) => {
            const { x, y, z } = wPos; CORNER_PTS.push([x / inch2m, -z / inch2m, y / inch2m]);
        });

        const cloneTest = testMesh.clone();
        cloneTest.position.set(position.x, position.y, position.z);
        parentRoof.add(cloneTest);
        const worldPos = cloneTest.getWorldPosition(new THREE.Vector3());
        parentRoof.remove(cloneTest);
        const CENTER_PT = [
            GetRoundNum(worldPos.x / inch2m, 4),
            GetRoundNum(-worldPos.z / inch2m, 4),
            GetRoundNum(worldPos.y / inch2m, 4)
        ];

        const NORMAL = [planeNor.x, -planeNor.z, planeNor.y];

        MODULES['M' + idx] = {
            CENTER_PT,
            CORNER_PTS,
            NORMAL,
            SOLAR_ACCESS: irrPro,
            ORIENTATION: dir === 'port' ? 'port' : 'land',
            IS_BALLASTED: module.userData.isBallastedRackType || false,
            BALLAST_DIR: getBallastDir(dir, module.userData.isBallastFlipped),
            BALLAST_TYPE: module.userData.ballastRackDirection !== undefined ? (module.userData.ballastRackDirection + "-rack") : "N/A",
            EFFICIENCY: efficiency || 0,
            PRODUCTION_MULTIPLIER: multiplier || 0,
            PLANE: null
        }
    });

    //FACES
    facesArr.forEach((faceItem, faceIdx) => {
        const { faceLineArr, posArr, size, facePtArr } = faceItem;
        const { norPos } = GetSizePosArr(posArr);
        FACES['F' + faceIdx] = {
            LINES: faceLineArr,
            NORMAL: [norPos.x, norPos.z, norPos.y],
            PLANE: [],
            AREA: size,
            PATH: facePtArr,
            WALL: null,
            STREET_SIDE: null
        };
    });

    //LINES
    lineArr.forEach(line => {
        const { lineKey, TYPE, ptKeyArr, STREET_SIDE, ENFORCED_PATHWAY, ENFORCED_ROOFS } = line;
        LINES[lineKey] = { TYPE, PTS: ptKeyArr, STREET_SIDE, ENFORCED_PATHWAY, ENFORCED_ROOFS };
    });

    //PTS
    ptArr.forEach(pt => {
        const { ptKey, pos } = pt;
        PTS[ptKey] = [
            GetRoundNum(pos.x / inch2m, 4),
            GetRoundNum(-pos.z / inch2m, 4),
            GetRoundNum(pos.y / inch2m, 4)
        ];
    });

    //SETBACKS
    self.roofMeshArr.forEach((roofMesh) => {
        const { lines } = roofMesh;
        const ridgeLines = GetHighestRidges(lines);

        if (!ShouldAddSetbackToRoof(roofMesh)) {
            return;
        }

        const isGivenBuildingMarkedAsDetached = useViewerStore.getState().detachedStructureBuildingIds.includes(Number(roofMesh.userData.buildingName));

        if (isGivenBuildingMarkedAsDetached) return;

        lines.forEach((line: any, lineIdx: number) => {
            const { setback, setbackInfo } = line;

            if (!setback || !setbackInfo || !setbackInfo[`pos${setback}`]) return;

            const setbackMesh = roofMesh.children.find((c: any) => c.setbackId === line.lineId);
            if (!setbackMesh) return;

            const points = line.points;
            let setbackDis = setback * inch2m;
            if (setbackMesh.userData.use18) setbackDis = 18 * inch2m;

            const lineStart = toVector(points[0]);
            const lineEnd = toVector(points[1]);
            const lineVector = new THREE.Vector3().subVectors(lineStart, lineEnd);

            const isRidge = line.type === 'RIDGE';
            const isEave = line.type === 'EAVE';
            let direction;

            if (isRidge || isEave) {
                const previousLine = lineIdx !== 0 ? lines[lineIdx - 1] : lines[lines.length - 1];
                const previousLineDir = toVector(previousLine.points[0]).sub(lineStart).normalize();
                const planeNormal = new THREE.Vector3().crossVectors(lineVector, previousLineDir);

                if (planeNormal.y < 0) planeNormal.negate();

                direction = new THREE.Vector3()
                    .crossVectors(planeNormal, lineVector)
                    .normalize()
                    .multiplyScalar(setbackDis);

            } else {
                direction = new THREE.Vector3()
                    .crossVectors(new THREE.Vector3(0, 1, 0), lineVector)
                    .normalize()
                    .multiplyScalar(setbackDis);

                if (direction.y > 0) direction.negate();
            }

            if (['EAVE', 'VALLEY', 'HIP', 'RAKE', "RIDGE"].includes(line.type)) {
                direction.negate();
            }

            const origSetbackLineStart = lineStart.clone().add(direction);
            const origSetbackLineEnd = lineEnd.clone().add(direction);

            const findIntersectingLine = (referencePoint: THREE.Vector3, incrementIterate: boolean) => {
                let count = 0;
                let directionFlag = 0;
                let intersectLine;
                let intersectPoint;
                let maxDistanceSquared = 0;

                for (const { item } of iterateCircular(lines, lineIdx, incrementIterate)) {
                    if (count === 0) {
                        count++;
                        continue;
                    }

                    const p0 = toVector(item.points[0]);
                    const p1 = toVector(item.points[1]);

                    if (count === 1) directionFlag = Math.sign(p0.clone().sub(p1).y);

                    count++;

                    if (Math.sign(p0.clone().sub(p1).y) !== directionFlag) break; // same-direction only

                    const intersect = doLinesIntersect(
                        origSetbackLineStart,
                        origSetbackLineEnd,
                        p0,
                        p1,
                        true,
                        true,
                        0.12
                    );

                    if (intersect) {
                        const distSqr = intersect.distanceToSquared(referencePoint);
                        if (distSqr > maxDistanceSquared) {
                            intersectLine = item;
                            intersectPoint = intersect;
                            maxDistanceSquared = distSqr;
                        }
                    }
                }

                return intersectLine
                    ? { line: intersectLine, point: intersectPoint }
                    : null;
            };

            let setbackLineStart = origSetbackLineStart;
            let setbackLineEnd = origSetbackLineEnd;

            const intersectStart = findIntersectingLine(origSetbackLineEnd, false);
            if (intersectStart?.point) setbackLineStart = intersectStart.point;

            const intersectEnd = findIntersectingLine(origSetbackLineStart, true);
            if (intersectEnd?.point) setbackLineEnd = intersectEnd.point;

            setbackPts.push([
                [setbackLineStart.x / inch2m, -setbackLineStart.z / inch2m, setbackLineStart.y / inch2m],
                [setbackLineEnd.x / inch2m, -setbackLineEnd.z / inch2m, setbackLineEnd.y / inch2m]
            ]);
        });


    });

    const genJson = {
        SITE_INFO: {
            GeoReference: {
                GeoReferenceNorthAngle: 0,
                Latitude: latitude,
                Longitude: longitude,
                LocationSource: "Manual",
                Map: "terrain",
                ModelTranslationX: null, // -18871519.616960514,
                ModelTranslationY: null, // -174402260.58333763,
                PeakHeight: buildingAlt!.high,
                UsesGeoReferencing: false
            },
            temp: { temp: 0 },
            Box: {
                acc_aurora_id: null,
                acc_root_id: null,
                opp_root_id: null,
                opp_site_info_designs_id: null,
                opp_site_info_documents_id: null
            }, // "162919310082"
            Location: {
                address: useViewerStore.getState().siteAddress,
                city: city,
                "full address": useViewerStore.getState().siteAddress,
                lat: latitude,
                lon: longitude,
                "north orientation": "0",
                postal: postalCode,
                state: state
            },
            Eagleview: {
                house_center: [0, 0, buildingAlt!.high],
                includes_trees: false
            },
            Genesis: { version: "Genesis 2" },
            Genesis_Entities: { Protected_IDs: [ /* ... integer array */] },
            Setbacks: { EAVE, HIP, RAKE, RIDGE, STEPFLASH, VALLEY },
            Module_Info: {
                Depth: selModuleIdx,
                Height: selModuleInfo.h / inch2m,
                Wattage: selModuleInfo.power,
                Width: selModuleInfo.w / inch2m
            },
            TMY2: {
                city,
                distance: null,
                elev: null,
                lat: latitude,
                location: detail,
                lon: longitude,
                solar_resource_file: "14737.tm2",
                state,
                tz: sunInfo.utcOffset
            },
            TMY3: {
                city,
                distance: null,
                elev: null,
                lat: latitude,
                location: detail,
                lon: longitude,
                solar_resource_file: csvFileName,
                state,
                tz: sunInfo.utcOffset
            },
            PVWatts: { "ideal_prod_multiplier": idealProdMulti },
            Utility: { api_names: null, api_rate: null },
            Sun_Angles: { hash: sunAngles },
            NSRDB: { "Updated": null },
            GSU_ContributorsInfo: { "LastModifiedByKey": "", "VersionKey": null, }, // 1000
            Sunnova: { "LID": null },
            RSA: { "modified_by": {}, "status": null, "verified": null },
            Walls: { "driveway selected": true, "front facing selected": true },
            Salesforce: {
                "Account Name": null, // "9613 Viceroy Lane  Breinigsville  PA",
                "Annual Usage": null, // 16301,
                "Customer Email": null, // "12tinkpeter@gmail.com",
                "Customer First Name": null, // "Suzanne",
                "Customer Last Name": null, // "Gohn-Nafus",
                "Customer Phone": null, // "(610) 809-4845",
                "Mod Picklist": moduleList.reduce((p: any, c: any) => {
                    p[c.value] = c.power;
                    return p;
                }, {}),
                "Module Type": selModuleInfo.value,
                "Opportunity Id": sessionStorage.getItem('SalesForceId'), // "0065b00000v547qAAA",
                "Opportunity Name": sessionStorage.getItem('opportunityName'), // "Gohn  Suzanne-",
                "Owner": null, // "Trinity Direct",
                "Postal Code": postalCode,
                "RSA Picklist": null,
                "Setback Info": {
                    "Eave__c": null,
                    "Valley__c": null,
                    "Hip__c": null,
                    "Rake__c": null,
                    "Ridge__c": null,
                    "All_Roofs__c": null,
                    "Setback_Notes__c": null,
                    "Designer_Needed__c": null,
                    "Front_of_Home__c": false,
                    "Doors__c": false,
                    "Single_Ridge__c": false,
                    "Alternate_Venting__c": false,
                    "X24_Access_Roof__c": false,
                    "Perimeter__c": false,
                    "Street_Side_Pathway__c": true
                },
                "Site Assessment": null, // "Remote",
                "Stage": null, // "5",
                "System Productions Id": null, // "a5v5b0000012qojAAA",
                "Township Name": null, // "Upper Macungie Township, PA",
                "Utility Name": null, // "PPL Electric Utilities"
            }
        },
        ROOFS, FACES, LINES, PTS, TREES, OBSTRUCTIONS, MODULES, SETBACKS: { "PTS": setbackPts },
        TERRAIN: {
            img_bins: { "terrain.jpg": map64Binary, "snap.jpg": null }, // ,
            "uvs": { "terrain.jpg": map64Binary, "snap.jpg": null }
        }
    }
    return genJson;
}

function toVector(v: { x: number, y: number, z: number }) {
    return new THREE.Vector3(v.x, v.y, v.z);
}

function GetObstInfo(obst: any, endL: number, endP: number, selRoof: any) {

    const { worldPos, shapeNum, verPosArr, radius, height, verFloor } = obst, { x, y, z } = worldPos;
    const verArr: {
        bottom: { x: number; y: number; z: number }[];
        ceiling: { x: number; y: number; z: number }[]
    } = { bottom: [], ceiling: [] };

    // const verArr = { bottom: [], ceiling: [] };
    if (verFloor) {
        const verPlaneArr: { x: number, z: number }[] = [], interYArr: number[] = [];
        if (shapeNum === 1) {
            verPosArr.forEach((wPos: THREE.Vector3) => {
                verPlaneArr.push({
                    x: GetRoundNum(wPos.x, 4),
                    z: GetRoundNum(wPos.z, 4)
                });
            });
        } else {
            verNorArr.forEach(verNor => {
                verPlaneArr.push({
                    x: GetRoundNum(x + verNor.x * radius / 1.6, 4),
                    z: GetRoundNum(z + verNor.z * radius / 1.6, 4)
                });
            });
        }
        verPlaneArr.forEach(verPlane => {
            if (!selRoof) { interYArr.push(0); return; }
            const posStart = new THREE.Vector3(verPlane.x, y + 10, verPlane.z),
                posTarget = new THREE.Vector3(verPlane.x, y - 10, verPlane.z),
                sunLineVec = new THREE.Vector3();
            sunLineVec.subVectors(posTarget, posStart);
            sunLineVec.normalize();
            const raycaster = new THREE.Raycaster();
            raycaster.set(posStart, sunLineVec);
            const rayIntersect = raycaster.intersectObjects([selRoof], false)[0];
            var interY = y;
            if (!rayIntersect) { }
            else {
                const { point } = rayIntersect;
                interY = GetRoundNum(point.y, 4);
            }
            interYArr.push(interY);
        });
        [0, height].forEach(h => {
            const alt = h + y;
            verPlaneArr.forEach((verPlane, verIdx) => {
                var posY = alt;
                if (interYArr && h === 0) {
                    posY = interYArr[verIdx];
                }
                const ptPos: { x: number, y: number, z: number } = { x: verPlane.x, y: posY, z: verPlane.z }, sideKey = h === 0 ? 'bottom' : 'ceiling';
                verArr[sideKey].push(ptPos);
            });
        });
    } else {
        const norArr = shapeNum === 1 ? [{ x: -1, z: -1 }, { x: -1, z: 1 }, { x: 1, z: 1 }, { x: 1, z: -1 }] : verNorArr;
        [-1, 1].forEach(h => {
            norArr.forEach(nor => {
                const cloneTest = testMesh.clone();
                cloneTest.position.set(nor.x * 0.5, h * 0.5, nor.z * 0.5);
                obst.add(cloneTest);
                const wPos = cloneTest.getWorldPosition(new THREE.Vector3()), sideKey = h === -1 ? 'bottom' : 'ceiling';
                const wPosCut = { x: GetRoundNum(wPos.x, 4), y: GetRoundNum(wPos.y, 4), z: GetRoundNum(wPos.z, 4) };
                verArr[sideKey].push(wPosCut);
                obst.remove(cloneTest);
            });
        });
    }

    const newFaceArr: any[] = [];
    const newLineArr: any[] = [];
    const newPtArr: any[] = [];

    if (shapeNum === 4) {

        const geometry = obst.geometry;

        const { topTris, bottomTris, sideTris } = parseExtrudedPolygon(geometry);


        for (let i = 0; i < topTris.length; i += 3) {
            const va = topTris[i], vb = topTris[i + 1], vc = topTris[i + 2];
            const { usedLines, usedPoints } = addTriangleAsFace(va, vb, vc, newLineArr, newFaceArr, newPtArr, endL, endP);
            endL += usedLines;
            endP += usedPoints;
        }

        for (let i = 0; i < bottomTris.length; i += 3) {
            const va = bottomTris[i], vb = bottomTris[i + 1], vc = bottomTris[i + 2];
            const { usedLines, usedPoints } = addTriangleAsFace(va, vb, vc, newLineArr, newFaceArr, newPtArr, endL, endP);
            endL += usedLines;
            endP += usedPoints;
        }

        for (let i = 0; i < sideTris.length; i += 3) {
            const va = sideTris[i], vb = sideTris[i + 1], vc = sideTris[i + 2];
            const { usedLines, usedPoints } = addTriangleAsFace(va, vb, vc, newLineArr, newFaceArr, newPtArr, endL, endP);
            endL += usedLines;
            endP += usedPoints;
        }


        return { newFaceArr, newLineArr, newPtArr };

    }

    return GetPolygonVerArr(verArr, endL, endP);
}

function GetPolygonVerArr(verArr: any, endL: any, endP: any) {
    const fixEndP = endP, fixEndL = endL;
    const newFaceArr: any[] = [], newLineArr: any[] = [], newPtArr: any[] = [], verCount = verArr.bottom.length;
    ['bottom', 'ceiling'].forEach(sideKey => {
        const udFaceLineKeyArr: any[] = [], udFacePosArr: any[] = [], udFacePtKeyArr: any[] = [];
        verArr[sideKey].forEach((verItem: any, verIdx: number) => {
            const lastVer = verIdx === verCount - 1;
            const ptKey = 'P' + endP, nextPtKey = lastVer ? 'P' + (endP - verCount + 1) : 'P' + (endP + 1); //  - 1
            const nextIdx = lastVer ? 0 : verIdx + 1, nextVer = verArr[sideKey][nextIdx];
            newPtArr.push({ ptKey, pos: { ...verItem } });
            endP++;
            const ptKeyArr = [ptKey, nextPtKey], lineKey = 'L' + endL;
            const points = [{ ...verItem }, { ...nextVer }];
            newLineArr.push({ ENFORCED_PATHWAY: false, ENFORCED_ROOFS: [], STREET_SIDE: false, TYPE: false, lineKey, ptKeyArr, posArr: points });
            endL++;
            udFaceLineKeyArr.push(lineKey);
            udFacePosArr.push({ ...verItem });
            udFacePtKeyArr.push(ptKey);
        });
        newFaceArr.push({ faceLineArr: udFaceLineKeyArr, posArr: udFacePosArr, size: null, facePtArr: udFacePtKeyArr });
    });

    verArr.bottom.forEach((verPlane: any, verIdx: number) => {
        const linePillarKey = 'L' + endL,
            linePtKeyArr = [
                'P' + parseInt(fixEndP + verIdx),
                'P' + parseInt(fixEndP + verIdx + verCount)
            ],
            linePoints = [
                { ...verArr.ceiling[verIdx] },
                { ...verArr.bottom[verIdx] }
            ];
        newLineArr.push({ ENFORCED_PATHWAY: false, ENFORCED_ROOFS: [], STREET_SIDE: false, TYPE: false, lineKey: linePillarKey, ptKeyArr: linePtKeyArr, posArr: linePoints });
        endL++;
    });

    verArr.bottom.forEach((verPlane: any, verIdx: number) => {
        const lastVer = verIdx === verCount - 1;
        var pos0 = newPtArr[verIdx], pos1 = newPtArr[verIdx + 1], pos2 = newPtArr[verIdx + verCount + 1], pos3 = newPtArr[verIdx + verCount];
        var line0 = newLineArr[verIdx], line1 = newLineArr[verIdx + verCount * 2 + 1], line2 = newLineArr[verIdx + verCount], line3 = newLineArr[verIdx + verCount * 2];
        if (lastVer) {
            pos1 = newPtArr[0]; pos2 = newPtArr[verCount];
            line1 = newLineArr[verIdx + verCount + 1];
        }
        if (line0 && line1 && line2 && line3) {
            const facelineKeyArr = [line0.lineKey, line1.lineKey, line2.lineKey, line3.lineKey];
            const facePoints = [pos0.pos, pos1.pos, pos2.pos, pos3.pos],
                facePtKeyArr = [pos0.ptKey, pos1.ptKey, pos2.ptKey, pos3.ptKey];
            newFaceArr.push({ faceLineArr: facelineKeyArr, posArr: facePoints, size: null, facePtArr: facePtKeyArr });
        }
    });

    return { newFaceArr, newLineArr, newPtArr };
}

export function SetMatEmissive(selectArr: any[], mainType: string, selType?: string, mouseType?: string) {
    const availableTypeArr = ['tree', 'obst'];
    if (!availableTypeArr.includes(mainType)) return;
    const selMeshArr = selectArr.filter(item => {
        return item.meshType === mainType
    });
    selMeshArr.forEach(item => {
        if (!item.isMesh) return;
        var emissiveCol = 0x000000;
        if (mainType === 'tree') {
        }
        else {
            if (selType === 'allSelected') {
                if (mainType === 'obst') emissiveCol = 0xff0000;
            }
            item.material.emissive.set(emissiveCol);
        }
    });
}

function EncryptJsonStr(text: string) {
    const symbols = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz1234567890!@#$%^&*()_-+={}[]|:;'<>,.?/ ".split("");
    const encSymbols = "@F_3y4X%eBjxVU{5<(,RdKf#=IPDJLA9'} Ho[|iThrN^ngv+sSWE*C6m)t-b?O2]qk8z>:Qwc0!Y;&la7uMp1$.Z/G".split("");
    let hash: { [key: string]: string } = {};
    for (let i = 0; i < symbols.length; i++) {
        hash[symbols[i]] = encSymbols[i];
    }
    let newStr = "";
    for (let i = 0; i < text.length; i++) {
        let char = text[i];
        let newChar = hash[char];
        if (newChar) {
            newStr += newChar;
        } else {
            newStr += char;
        }
    }
    return newStr;
}

export function GetMousePos(e: any) { // TouchEvent | MouseEvent
    let posX, posY;

    const canvas = document.querySelector('canvas') as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();

    if (e.touches && e.touches[0]) {
        posX = e.touches[0].clientX - rect.left;
        posY = e.touches[0].clientY - rect.top;
    } else if (e.changedTouches && e.changedTouches[0]) {
        posX = e.changedTouches[0].clientX - rect.left;
        posY = e.changedTouches[0].clientY - rect.top;
    } else if (e.offsetX !== undefined && e.offsetY !== undefined) {
        posX = e.offsetX;
        posY = e.offsetY;
    } else {
        posX = e.clientX - rect.left;
        posY = e.clientY - rect.top;
    }
    return { posX, posY };
}

export function random(min: number, max: number) {
    return Math.random() * (max - min) + min;
}

export const ifRoofDeactivated = (roofID: number) => {
    const deactiveRoofIdArr = useViewerStore.getState().deactivatedRoofIdArr
    return deactiveRoofIdArr.includes(Number(roofID))
}


export const refreshContextMenu = () => {
    useViewerStore.setState((state) => ({ refreshContextMenu: !state.refreshContextMenu }));
}

const hideSetbackAndPathwaysForGivenRoof = (roof: RoofMesh) => {
    roof.children.forEach((child: any) => {
        if (child.meshType === 'setback' || child.meshType === 'pathway') {
            child.visible = false;
        }
    })
}

const reEnableSetbackAndPathwaysForGivenRoof = (roof: RoofMesh) => {
    roof.children.forEach((child: any) => {
        if (child.meshType === 'setback' || child.meshType === 'pathway') {
            child.visible = true;
        }
    })
}

export const updateRoofsAltered = (roofId: number) => {
    const roofsAltered = useViewerStore.getState().roofsAltered;
    const isAlreadyAltered = roofsAltered.includes(roofId);
    useViewerStore.setState({ //Check if roof was altered/
        roofsAltered: isAlreadyAltered
            ? roofsAltered.filter((id) => id !== roofId) // Remove roof if it was already altered from original state
            : [...roofsAltered, roofId],  //Add roof if it was not yet altered
    }               
    );
}

export const reActivateRoof = (roof: RoofMesh) => {
    const roofID = roof.userData.roof_tag_id;
    const deactiveRoofIdArr = useViewerStore.getState().deactivatedRoofIdArr;
    const filteredArr = deactiveRoofIdArr.filter(id => id !== Number(roofID));
    useViewerStore.setState({ deactivatedRoofIdArr: filteredArr });
    roof.disableModule = false;
    updateRoofsAltered(roofID);
    recalculate33PercentModuleCount();
    refreshContextMenu();
    reEnableSetbackAndPathwaysForGivenRoof(roof);
    RefreshShade();
}

export const deActivateRoof = (roof: RoofMesh, isSavedDesignLoad = false) => {
    const sceneViewer = useViewerStore.getState().sceneViewer;
    const roofID = roof.userData.roof_tag_id;
    const deactiveRoofIdArr = useViewerStore.getState().deactivatedRoofIdArr;
    if (!deactiveRoofIdArr.includes(Number(roofID))) {
        useViewerStore.setState({
            deactivatedRoofIdArr: [...deactiveRoofIdArr, Number(roofID)]
        });
    }
    roof.disableModule = true;
    if (!isSavedDesignLoad) {
        sceneViewer.setClearFaceModule(roof);
        recalculate33PercentModuleCount()
    }
    updateRoofsAltered(roofID)
    hideSetbackAndPathwaysForGivenRoof(roof)
    refreshContextMenu()
    hideSetbackAndPathwaysForGivenRoof(roof)
    RefreshShade()
}

export const recalculate33PercentModuleCount = () => {
    const {
        sceneViewer,
        deactivatedRoofIdArr,
        activeModule: selSize,
        buildingWise33PercentRuleModuleCountList,
    } = useViewerStore.getState();

    const roofMeshArr = sceneViewer.roofMeshArr;

    const updatedArr: BuildingWise33PercentRuleInfo[] = buildingWise33PercentRuleModuleCountList.map(b => ({
        ...b,
        size: 0,
        sizeSqFt: 0,
    }));

    roofMeshArr.forEach((roofMesh: any) => {
        const roofID = Number(roofMesh.userData.roof_tag_id);
        if (roofMesh.disableModule || deactivatedRoofIdArr.includes(roofID)) return;

        const { sizeM } = roofMesh;
        const { buildingName } = roofMesh.userData;

        let rec = updatedArr.find(b => b.name === buildingName);
        if (!rec) {
            rec = {
                name: buildingName,
                size: 0,
                sizeSqFt: 0,
                isFlatRoof: !!roofMesh.flatRoof,
                moduleArea: 0,
                modeCount33: 0,
                modeCount: 0,
                mode33Over: false,
            };
            updatedArr.push(rec);
        }

        rec.size += sizeM;
        rec.sizeSqFt += sizeM * 10.7639;
    });


    const correctedModuleWidth = selSize.w + inch2m;
    const correctedModuleHeight = selSize.h + inch2m;
    const moduleAreaInSqInch =
        correctedModuleWidth * 39.3701 *
        correctedModuleHeight * 39.3701;

    updatedArr.forEach(rec => {
        const buildingAreaInSqInch = rec.size * 1550;
        rec.moduleArea = moduleAreaInSqInch / 144;
        rec.modeCount33 = Math.ceil(
            (buildingAreaInSqInch * 0.33) / moduleAreaInSqInch
        );
    });


    useViewerStore.setState({ buildingWise33PercentRuleModuleCountList: updatedArr });
    sceneViewer.buildingArr = [...updatedArr];
};


export const syncDeactivateRoofForSavedDesign = () => {
    const deactiveRoofIdArr = useViewerStore.getState().deactivatedRoofIdArr;
    const sceneViewer = useViewerStore.getState().sceneViewer;
    sceneViewer.roofMeshArr.forEach((roof: any) => {
        const roofID = roof.userData.roof_tag_id;
        if (deactiveRoofIdArr.includes(Number(roofID))) {
            deActivateRoof(roof, true);
        }
    });
}

export const setDeactivateRoofArrInStoreFromDesignData = (designList: any[], index: number, deactivatedRoofIdArray: number[] = []) => {
    let deactivatedRoofIdArr: number[] = [];

    if (index === 0) {
        designList[index].Buildings[0].Roofs.forEach((roof: any) => {
            if (roof.IsDeactivated) {
                deactivatedRoofIdArr.push(Number(roof.Designation.replace(/\D/g, '')))
            }
        });
    } else {
        deactivatedRoofIdArr = deactivatedRoofIdArray
    }
    useViewerStore.setState({ deactivatedRoofIdArr: deactivatedRoofIdArr });
}